'use client';

import { useCallback, useRef, useState } from 'react';

/**
 * Rabatt-Code als Kopier-Button der Festival-Sektion (`fest-code`, 1:1 aus dem
 * Prototyp). Kopiert den Code in die Zwischenablage und toggelt die
 * `is-copied`-Klasse – die CSS blendet dann `.lbl-copy` aus und `.lbl-copied`
 * ein. Einziger Client-Anteil der ansonsten server-gerenderten Festival-Sektion.
 */
export function CopyCode({
  code,
  label,
  copyHint,
  copiedLabel,
}: {
  code: string;
  label: string;
  copyHint: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCopy = useCallback(() => {
    if (!navigator.clipboard) return;

    void navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Zwischenablage nicht verfuegbar (z.B. verweigerte Permission) –
        // still ignorieren, der Code bleibt sichtbar und manuell markierbar.
      });
  }, [code]);

  return (
    <button
      type="button"
      className={copied ? 'fest-code is-copied' : 'fest-code'}
      data-code={code}
      onClick={onCopy}
      aria-label={`${code} – ${copyHint}`}
    >
      <span className="fest-code-code">{code}</span>
      <span className="fest-code-desc">{label}</span>
      <span className="fest-code-copy" aria-live="polite">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="9" y="9" width="12" height="12" rx="2" />
          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
        <span className="lbl-copy">{copyHint}</span>
        <span className="lbl-copied">{copiedLabel}</span>
      </span>
    </button>
  );
}
